import { DateWrapper } from '@trucktrax/trucktrax-common';
import {
  DriverDto,
  GeoZoneDto,
  OrderDto,
  PlantDto,
  TicketDto,
  TruckDto,
} from '@trucktrax/trucktrax-ts-common';
import { isEmpty } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { TICKETS_SELECTED } from '../../../constants/appConstants';
import { logoutDriver } from '../../../services/authService';
import { disableTabNavigation, enableTabNavigation } from '../../../services/keyboardService';
import { trackingSocketInit } from '../../../services/mapviewsService';
import {
  selectOrderMarker,
  selectRouteMarker,
  selectTruckMarker,
} from '../../../services/ticketsService';
import { ReduxState } from '../../../store';
import {
  clearSelectedMarker,
  closePopup,
  MarkerInfo,
  MarkerSelectedFrom,
  selectMarker,
  setIsPanned,
} from '../../../store/actions/mapviewActions';
import setSidebarSelected from '../../../store/actions/sidebarActions';
import { clearSelectedTicket, selectTicket } from '../../../store/actions/ticketActions';
import { determineRegionId } from '../../../util/appUtil';
import {
  ConnectedDispatchFunction,
  LogoutDriverInfo,
  MapMarker,
  MapMarkerType,
  Marker,
  SidebarTitleType,
} from '../../../types';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import { isMapMarker, isMarkerInfo, isMarkerType } from '../../../util/markerUtil';
import { getValidOrdersListInDateRange } from '../../../util/orderUtil';
import { retrieveProductLineNameDiff } from '../../../util/statusesUtil';
import { DefaultLocation } from './mapTypes';
import styles from './MapWrapper.module.css';
import TTMap, { MarkerDataType, MarkerType } from './TTMap';
import { isValidLatLng } from '../../../util/geoUtil';

export class MapWrapper extends Component<MapWrapperAllProps, MapWrapperState> {
  static defaultProps: Partial<MapWrapperAllProps> = {
    currentRegion: { url: '', defaultRegion: false },
    selectedMarker: {
      markerId: '0',
      lat: 1,
      lng: 1,
      isPannedTo: false,
    },
    positions: [],
    sidebarSelection: undefined,
    ordersList: [],
    currentGeozone: undefined,
  };

  private map: React.RefObject<HTMLInputElement>;

  constructor(props: MapWrapperAllProps) {
    super(props);
    this.map = React.createRef();
    this.state = {
      tabNavigationListener: null,
    };
  }

  componentDidMount() {
    this.setState({ tabNavigationListener: disableTabNavigation() as any });
    trackingSocketInit(this.props.currentRegion!.url!);
  }

  async componentDidUpdate(prevProps: MapWrapperAllProps) {
    const { showJobsites, currentRegion, ordersStartDate, ordersEndDate, selectedProductLine } =
      this.props;

    const regionChanged = prevProps.currentRegion.url !== currentRegion.url;
    const orderStartDateChanged = prevProps.ordersStartDate !== ordersStartDate;
    const orderEndDateChanged = prevProps.ordersEndDate !== ordersEndDate;
    const selectedProductLineChanged = prevProps.selectedProductLine !== selectedProductLine;
    const shouldToggleOrderListOn =
      showJobsites &&
      (!prevProps.showJobsites ||
        regionChanged ||
        orderStartDateChanged ||
        orderEndDateChanged ||
        selectedProductLineChanged);

    if (shouldToggleOrderListOn && this.props.ordersStartDate && this.props.ordersEndDate) {
      this.props.orderListOn(this.props.ordersStartDate, this.props.ordersEndDate);
    }

    const shouldToggleOrderListOff = !prevProps.showJobsites && this.props.showJobsites;
    if (shouldToggleOrderListOff) {
      this.props.orderListOff();
    }
  }

  componentWillUnmount() {
    enableTabNavigation(this.state.tabNavigationListener);
  }

  // filters date range, duplicated addresses and renders if zone and radius exist
  getValidOrders = () => {
    const { ordersStartDate, ordersEndDate, ordersList } = this.props;
    const startDate = new DateWrapper(ordersStartDate).startOfDay.date;
    const endDate = new DateWrapper(ordersEndDate).endOfDay.date;
    const ordersMatchingPlants =
      ordersList?.filter(order => {
        const plant = this.props.plantListFilter.find(plant => plant.url === order.plant.url);
        return plant ? plant.isSelected : true;
      }) ?? [];
    const filteredOrders = MapWrapper.filterOrdersExcludeFOB(ordersMatchingPlants);
    return getValidOrdersListInDateRange(filteredOrders ?? [], startDate, endDate);
  };

  // https://trucktrax.atlassian.net/browse/DEV-2393 requests that FOB orders are removed so that they are not displayed
  static filterOrdersExcludeFOB = (orders: OrderDto[]): OrderDto[] =>
    orders?.filter((order: OrderDto) => !order.fob) ?? [];

  handleMarkerClick = (targetMarker: MarkerType | MarkerInfo | MapMarker) => {
    if (targetMarker.type === MapMarkerType.Order) {
      this.props.selectOrderMarker(targetMarker as MarkerType);
      return;
    }

    if (targetMarker.type === MapMarkerType.Plant) {
      this.props.selectMarker(targetMarker as unknown as MarkerInfo);
      return;
    }

    let ticketUrl: string = '';
    let truckAlias: string = '';
    let logData: any = { id: targetMarker.markerId, type: targetMarker.type };

    if (isMarkerType(targetMarker)) {
      ticketUrl = (targetMarker.markerData as MarkerDataType)?.ticket?.url ?? '';
      truckAlias = targetMarker?.truckDetails?.trackedObject?.truckAlias ?? '';
      logData = {
        ...logData,
        driverId: (targetMarker.driverDetails as DriverDto)?.id,
        truckAlias: (targetMarker.truckDetails as TruckDto)?.truckAlias,
        ticketUrl: (targetMarker.markerData as MarkerDataType)?.ticket?.url,
      };
    } else if (isMapMarker(targetMarker)) {
      ticketUrl = targetMarker.data?.ticket?.url ?? '';
      truckAlias = targetMarker?.data.truckDetails?.truckAlias ?? '';
      logData = {
        ...logData,
        positionId: targetMarker.data?.positionDetails?.id,
        driverId: targetMarker.data.driverDetails?.id,
        truckAlias: targetMarker.data.truckDetails?.truckAlias ?? targetMarker.data.truckAlias,
        ticketUrl: targetMarker.data?.ticket?.url,
      };
    }

    if (isEmpty(ticketUrl) || isEmpty(truckAlias)) {
      recordLog(
        LogLevel.Debug,
        '[MapWrapper].handleMarkerClick marker does not have an associated ticket and/or truckAlias',
        { logData },
      );
    }

    this.props.selectTruckMarker(
      { ...targetMarker, selectedFrom: MarkerSelectedFrom.MapMarker } as MarkerType,
      ticketUrl,
    );
  };

  static determineSelectedMarkerLocation = (marker: unknown): DefaultLocation | undefined => {
    if (isMapMarker(marker)) {
      const lat = marker.data?.lat;
      const lng = marker.data?.lng;
      // Validate coordinates are finite numbers before returning
      if (isValidLatLng(lat, lng)) {
        return { lat, lng };
      }
      return undefined;
    }

    if (isMarkerType(marker)) {
      const lat =
        marker.positionDetails?.location.lat ?? marker.positionDetails?.location.latitude ?? 0;
      const lng =
        marker.positionDetails?.location.lng ?? marker.positionDetails?.location.longitude ?? 0;
      // Validate coordinates are finite numbers before returning
      if (isValidLatLng(lat, lng)) {
        return { lat, lng };
      }
      return undefined;
    }

    if (isMarkerInfo(marker)) {
      const lat = marker.lat ?? 0;
      const lng = marker.lng ?? 0;
      // Validate coordinates are finite numbers before returning
      if (isValidLatLng(lat, lng)) {
        return { lat, lng };
      }
      return undefined;
    }
    return undefined;
  };

  handleMarkerClose = (targetMarker: MarkerType) => {
    this.props.closePopup(
      targetMarker.markerId ?? '',
      this.props.currentRegionId,
      this.props.selectedProductLine,
    );
    this.props.clearSelectedMarker();
  };

  ticketInfoTicketsCallBack = (marker: Marker) => {
    this.props.clearSelectedTicket();
    this.props.selectTicket(marker.ticket as TicketDto);
    this.props.setSidebarSelected(TICKETS_SELECTED, true);
    this.props.ticketInfoTicketsCallBack(marker);
  };

  orderDetailsCallback = (targetMarker: MarkerType) => {
    this.props.closePopup(
      targetMarker.markerId ?? '',
      this.props.currentRegionId,
      this.props.selectedProductLine,
    );
    this.props.orderDetailsCallback(targetMarker);
  };

  handleCenterChanged = (center?: { lat: number; lng: number }) => {
    if (!center) {
      recordLog(LogLevel.Debug, '[handleCenterChange] can not center without a center');
      return;
    }
    this.props.handleCenterChange(center);
  };

  handleInfoTicketsCallback = (ticket: Marker): void => this.ticketInfoTicketsCallBack(ticket);

  render() {
    const plantList = this.props.showPlants ? this.filterPlantList() : [];
    const orderList = this.props.showJobsites ? this.filterOrderList() : [];

    return (
      <div className={styles.mapPane} data-test="region-map" ref={this.map}>
        <TTMap
          center={this.props.location}
          currentRegion={this.props.currentRegion!}
          plantList={plantList}
          popupToggleMap={this.props.popupToggleMap}
          ordersList={orderList}
          showPlantsGeofences={this.props.showPlantsGeofences}
          showJobsitesGeofences={this.props.showJobsitesGeofences}
          currentGeozone={this.props.currentGeozone}
          selectedProductLine={this.props.selectedProductLine}
          onMarkerClick={this.handleMarkerClick}
          onMarkerClose={this.handleMarkerClose}
          driverInfoMessageCallback={this.props.driverInfoMessageCallback}
          updateToFieldValueWithPlantDrivers={this.props.updateToFieldValueWithPlantDrivers}
          ticketInfoTicketsCallBack={this.handleInfoTicketsCallback}
          messageDriversOnOrder={this.props.messageDriversOnOrder}
          handleCenterChanged={this.handleCenterChanged}
          handleLogoutDriver={this.props.logoutDriver}
          orderDetailsCallback={this.orderDetailsCallback}
          markerStatusMap={retrieveProductLineNameDiff(this.props.selectedProductLine)}
          onZoomChange={this.props.handleZoomChange}
          currentZoom={this.props.currentZoom}
          onMapReady={this.props.onMapReady}
          isMessagingEnabled
        />
      </div>
    );
  }

  filterPlantList = (): PlantDto[] => {
    if (this.props.showPlants || this.props.showJobsites) {
      return this.props.plantList.filter(p => {
        const isOfSelectedProductline = p.productLine === this.props.selectedProductLine;
        const isActive = p.archived === false && p.deleted === false;
        const plant = this.props.plantListFilter.find(plant => plant.id === p.id);
        return isActive && isOfSelectedProductline && plant ? plant.isSelected : true;
      });
    }
    return [];
  };

  filterOrderList = (): OrderDto[] => (this.props.showJobsites ? this.getValidOrders() : []);
}

export interface SelectedMarker {
  markerId: string;
  lat: number;
  lng: number;
  isPannedTo?: boolean;
}

export interface FeatureList {
  featuresList: {
    features: any;
    allFlags: any[];
  };
}

export interface MapWrapperState {
  tabNavigationListener: any;
}

export interface MapWrapperOwnProps {
  location: { lat: number; lng: number };
  driverInfoMessageCallback: (driver?: DriverDto) => void;
  updateToFieldValueWithPlantDrivers: (url: string) => void;
  ticketInfoTicketsCallBack: (ticket: Marker) => void;
  orderDetailsCallback: (orderDetail: MarkerType) => void;
  messageDriversOnOrder?: (order: OrderDto) => void;
  orderListOn: (arg0: Date, arg1: Date) => void;
  orderListOff: () => void;
  ordersList?: OrderDto[];
  currentGeozone?: GeoZoneDto;
  handleZoomChange: (zoomLevel: number) => void;
  currentZoom: number;
  handleCenterChange: (center: { lat: number; lng: number }) => void;
  onMapReady?: () => void;
}

function mapStateToProps(state: ReduxState) {
  return {
    selectedMarker: state.selectedMarker,
    popupToggleMap: state.popupToggleMap,
    currentRegion: state.currentRegion,
    positions: state.positionList,
    ticketsList: state.ticketsList,
    sidebarSelection: state.sidebar,
    flags: state.flags as any,
    plantList: state.plantList,
    trucks: state.trucksList.trucks,
    showTrucks: state.mapFilters.showTrucks,
    showPlants: state.mapFilters.showPlants,
    showJobsites: state.mapFilters.showJobsites,
    showPlantsGeofences: state.mapFilters.showPlantsGeofences,
    showJobsitesGeofences: state.mapFilters.showJobsitesGeofences,
    selectedProductLine: state.selectedProductLine,
    ordersStartDate: state.mapFilters.ordersStartDate,
    ordersEndDate: state.mapFilters.ordersEndDate,
    plantListFilter: state.mapFilters.plantList,
    showExtTrucks: state.mapFilters.showExtTrucks,
    userOverrides: state.userOverrides,
    userPermission: state.userPermission,
    allTrucks: state.allTrucks,
    activeMarkers: state.activeMarkers,
    currentRegionId: determineRegionId(state.currentRegion),
    currentGeozone: state.currentGeozone,
  };
}

interface MapWrapperDispatchProps {
  closePopup: ConnectedDispatchFunction<typeof closePopup>;
  clearSelectedMarker: () => void;
  clearSelectedTicket: () => void;
  selectTicket: (ticket: TicketDto) => void;
  setSidebarSelected: (sidebarTitle: SidebarTitleType, isSelected: boolean) => void;
  selectMarker: ConnectedDispatchFunction<typeof selectMarker>;
  selectTruckMarker: ConnectedDispatchFunction<typeof selectTruckMarker>;
  selectOrderMarker: ConnectedDispatchFunction<typeof selectOrderMarker>;
  logoutDriver: (logout: LogoutDriverInfo) => void;
}

type MapWrapperReduxStateProps = ReturnType<typeof mapStateToProps>;

export type MapWrapperAllProps = MapWrapperOwnProps &
  MapWrapperDispatchProps &
  MapWrapperReduxStateProps;

export default connect(mapStateToProps, {
  closePopup,
  clearSelectedMarker,
  selectTicket,
  setSidebarSelected,
  clearSelectedTicket,
  logoutDriver,
  setIsPanned,
  selectTruckMarker,
  selectMarker,
  selectOrderMarker,
  selectRouteMarker,
})(MapWrapper);
